import type {
	EquipmentInfoId,
	ExerciseInfo,
	ExerciseInfoId,
	MovementInfoId,
	PopulatedExerciseInfo,
} from '@az3fr/core';
import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/lib/store';
import { equipmentSliceSelectors } from '@/entities/equipment/model/equipment.selector';
import { exerciseAdapter } from '@/entities/exercise/model/exercise.slice';
import { movementSliceSelectors } from '@/entities/movement/model/movement.selector';
import { muscleSliceSelectors } from '@/entities/muscle/model/muscle.selector';
import { getCommonMuscleGroupName } from '@/entities/muscle/model/muscle-hierarchy.utils';

const baseSelectors = exerciseAdapter.getSelectors((state: RootState) => state.exercises);

/**
 * Populate the exercise with the movement (including muscles) and equipment
 */
const selectPopulatedExerciseById = createSelector(
	[
		baseSelectors.selectEntities, // dictionary of exercises
		equipmentSliceSelectors.selectEntities, // dictionary of equipments
		muscleSliceSelectors.selectEntities, // dictionary of all muscles (for hierarchy)
		(_state: RootState, exerciseId: ExerciseInfoId) => exerciseId,
		(state: RootState, exerciseId: ExerciseInfoId) => {
			const exercise = baseSelectors.selectById(state, exerciseId);
			if (!exercise) {
				return undefined;
			}
			return movementSliceSelectors.selectMovementWithMuscles(state, exercise.movementId);
		},
	],
	// Combinator function
	(exercises, equipments, allMuscles, exerciseId, movement) => {
		const exercise = exercises[exerciseId];
		if (!exercise) {
			return;
		}

		const equipment = equipments[exercise.equipmentId];

		// Only return if both movement and equipment are present
		if (!movement || !equipment) {
			return;
		}

		// Calculate the primary muscle group name using the common ancestor logic
		const firstPrimaryMuscle = movement.muscles.primary[0];
		const primaryMuscleGroupName = firstPrimaryMuscle?.commonName ?? firstPrimaryMuscle?.name ?? '';
		// ? getCommonMuscleGroupName(movement.muscles.primary, allMuscles)
		// : '';

		return {
			...exercise,
			movement, // MovementWithMusclesInfo (guaranteed to be present)
			equipment, // EquipmentInfo (guaranteed to be present)
			primaryMuscleGroupName, // Computed muscle group name from hierarchy
		};
	},
);

/**
 * Populate all exercises with the movement and equipment
 */
const selectAllPopulated = createSelector(
	[
		baseSelectors.selectAll, // array of exercises
		movementSliceSelectors.selectEntities, // dictionary of movements
		equipmentSliceSelectors.selectEntities, // dictionary of equipments
	],
	// Combinator function
	(exercises, movements, equipments): PopulatedExerciseInfo[] => {
		return exercises.map((exercise: ExerciseInfo) => {
			const movement = movements[exercise.movementId];
			const equipment = equipments[exercise.equipmentId];

			return {
				...exercise,
				movement, // MovementInfo || undefined (if not found/loaded yet)
				equipment, // EquipmentInfo || undefined (if not found/loaded yet)
			};
		});
	},
);

/**
 * Select an exercise by a pair of movement and equipment
 */
const selectByMovementAndEquipment = createSelector(
	[
		baseSelectors.selectAll, // array of exercises
		(_, movementId: MovementInfoId, _equipmentId: EquipmentInfoId) => movementId,
		(_, _movementId: MovementInfoId, equipmentId: EquipmentInfoId) => equipmentId,
	],
	// Combinator function
	(exercises, movementId, equipmentId): ExerciseInfo | undefined =>
		exercises.find(
			(exercise) => exercise.movementId === movementId && exercise.equipmentId === equipmentId,
		),
);

const exerciseSliceSelectors = {
	...baseSelectors,
	selectByMovementAndEquipment,
	selectPopulatedExerciseById,
	selectAllPopulated,
};

export { exerciseSliceSelectors };
